import React from "react";
import moment from "moment";
import { formatNumberWithCommas } from "../../../helpers";
import DropdownMenu from "../../ui/DropdownMenu";
import { MoreVertical } from "lucide-react";

// Utility for conditional Tailwind badges
const statusBadge = (value: string) => {
  let base = "inline-block px-3 py-1 rounded-full text-xs font-semibold ";
  switch (value?.toLowerCase()) {
    case "success":
    case "completed":
    case "active":
      return base + "bg-green-100 text-green-700";
    case "pending":
    case "processing":
      return base + "bg-yellow-100 text-yellow-700";
    case "failed":
    case "cancelled":
    case "inactive":
      return base + "bg-red-100 text-red-700";
    default:
      return base + "bg-gray-100 text-gray-700";
  }
};

const yesNoBadge = (bool: boolean) => {
  return bool
    ? "inline-block px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700"
    : "inline-block px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700";
};

// Transaction
export const transactionColumns = [
  // { label: "S/N", field: "id" },
  { label: "Type", field: "type" },
  { label: "Description", field: "description" },
  { label: "User", field: "user" },
  { label: "Status", field: "status" },
  { label: "Amount", field: "amount" },
  { label: "Fee", field: "fee" },
  { label: "Provider", field: "paymentProvider" },
  { label: "Ref ID", field: "refId" },
  { label: "Created At", field: "createdAt" },
  { label: "Action", field: "action" },
];

export const transactionRows = (data: any, morevertOptions: any) => {
  return data?.items?.map((item: any, index: number) => ({
    id: index + 1,
    type: item.type,
    user: item?.user?.lastName + " " + item?.user?.firstName,
    description: item.description,
    status: (
      <span className={`${statusBadge(item.status)}`}>{item.status}</span>
    ),
    amount: "₦ " + formatNumberWithCommas(item.amount),
    fee: "₦ " + formatNumberWithCommas(item.fee),
    paymentProvider: item.paymentProvider,
    refId: item.refId,
    createdAt: moment(item.createdAt).format("Do MMM YYYY"),
    "wallet.withdrawableBalance":
      "₦ " + formatNumberWithCommas(item.wallet?.withdrawableBalance ?? 0),
    "wallet.totalDeposit":
      "₦ " + formatNumberWithCommas(item.wallet?.totalDeposit ?? 0),
    "wallet.totalWithdrawal":
      "₦ " + formatNumberWithCommas(item.wallet?.totalWithdrawal ?? 0),
    action: (
      <DropdownMenu
        options={morevertOptions}
        icon={<MoreVertical style={{ cursor: "pointer" }} className="z-0" />}
        item={item}
      />
    ),
  }));
};

// Revenue
export const revenueColumns = [
  // { label: "S/N", field: "id" },
  { label: "Total Revenue", field: "totalRevenue" },
  { label: "Withdrawal Revenue", field: "totalWithdrawalRevenue" },
  { label: "Deposit Revenue", field: "totalDepositRevenue" },
  { label: "Purchase Revenue", field: "totalPurchaseRevenue" },
  { label: "Summary Date", field: "summaryDate" },
  { label: "Locked", field: "isLocked" },
];

export const revenueRows = (data: any) => {
  console.log(data);
  return data?.map((item: any, index: number) => ({
    id: index + 1,
    totalRevenue: "₦ " + formatNumberWithCommas(item.totalRevenue ?? 0),
    totalWithdrawalRevenue:
      "₦ " + formatNumberWithCommas(item.totalWithdrawalRevenue ?? 0),
    totalDepositRevenue:
      "₦ " + formatNumberWithCommas(item.totalDepositRevenue ?? 0),
    totalPurchaseRevenue:
      "₦ " + formatNumberWithCommas(item.totalPurchaseRevenue ?? 0),
    summaryDate: item.summaryDate
      ? moment(item.summaryDate).format("Do MMM YYYY")
      : "N/A",
    isLocked: (
      <span className={yesNoBadge(item.isLocked)}>
        {item.isLocked ? "Yes" : "No"}
      </span>
    ),
  }));
};

// Users
export const userColumns = [
  // { label: "S/N", field: "id" },
  { label: "Full Name", field: "fullName" },
  { label: "Username", field: "username" },
  { label: "Email", field: "email" },
  { label: "Status", field: "status" },
  { label: "Role", field: "roles" },
  { label: "User Type", field: "userType" },
  { label: "Email Verified", field: "emailVerified" },
  { label: "Phone Verified", field: "phoneVerified" },
  { label: "KYC Status", field: "kycStatus" },
  { label: "Language", field: "language" },
  { label: "Reg Date", field: "createdAt" },
];

export const userRows = (data: any) => {
  return data?.map((item: any, index: number) => ({
    id: index + 1,
    fullName:
      item.fullName ?? `${item.firstName ?? ""} ${item.lastName ?? ""}`.trim(),
    username: item.username,
    email: item.email,
    status: <span className={statusBadge(item.status)}>{item.status}</span>,
    roles: item.roles,
    userType: item.userType,
    emailVerified: (
      <span className={yesNoBadge(item.emailVerified)}>
        {item.emailVerified ? "Yes" : "No"}
      </span>
    ),
    phoneVerified: (
      <span className={yesNoBadge(item.phoneVerified)}>
        {item.phoneVerified ? "Yes" : "No"}
      </span>
    ),
    kycStatus: item.kycStatus,
    language: item.language ?? "N/A",
    createdAt: moment(item.createdAt).format("Do MMM YYYY"),
  }));
};

// Contracts
export const contractColumns = [
  { label: "S/N", field: "id" },
  { label: "Title", field: "title" },
  { label: "Contract Code", field: "contractCode" },
  { label: "Type", field: "type" },
  { label: "Vendor", field: "vendor" },
  { label: "Vendor Email", field: "vendorEmail" },
  { label: "Vendor Username", field: "vendorUsername" },
  { label: "Ship-out State", field: "shipoutState" },
  { label: "Estimated Weight (kg)", field: "estimatedWeight" },
  { label: "Actual Weight (kg)", field: "actualWeight" },
  { label: "Chat Screenshots", field: "chatScreenshotsCount" },
  { label: "Status", field: "status" },
  { label: "Buyer Approval", field: "buyerApprovalStatus" },
  { label: "Shipping Date", field: "shippingDate" },
  { label: "Est. Delivery Date", field: "estimatedDeliveryDate" },
  { label: "Created At", field: "createdAt" },
];

export const contractRows = (data: any) => {
  return data?.map((item: any, index: number) => ({
    id: index + 1,
    title: item.title,
    contractCode: item.contractCode,
    type: item.type,
    vendor: item.vendorId?.fullName ?? "N/A",
    vendorEmail: item.vendorId?.email ?? "N/A",
    vendorUsername: item.vendorId?.username ?? "N/A",
    shipoutState: item.shipoutState,
    estimatedWeight: item.estimatedWeight ?? "N/A",
    actualWeight: item.actualWeight ?? "N/A",
    chatScreenshotsCount: item.chatScreenshots?.length ?? 0,
    status: <span className={statusBadge(item.status)}>{item.status}</span>,
    buyerApprovalStatus: (
      <span className={statusBadge(item.buyerApprovalStatus)}>
        {item.buyerApprovalStatus}
      </span>
    ),
    shippingDate: moment(item.shippingDate).format("Do MMM YYYY"),
    estimatedDeliveryDate: moment(item.estimatedDeliveryDate).format(
      "Do MMM YYYY",
    ),
    createdAt: moment(item.createdAt).format("Do MMM YYYY"),
  }));
};

export const transactionStatusOptions = [
  { label: "All", value: "" },
  { label: "COMPLETED", value: "COMPLETED" },
  { label: "PENDING", value: "PENDING" },
  { label: "FAILED", value: "FAILED" },
  { label: "CANCELLED", value: "CANCELLED" },
  { label: "EXPIRED", value: "EXPIRED" },
];

// Revenue Analytics Table (shared columns for Detailed Analytics & Transactions)
export const revenueAnalyticsTableColumns = [
  { label: "S/N", field: "sn" },
  { label: "Date", field: "date" },
  { label: "Total revenue", field: "totalRevenue" },
  { label: "Deposits", field: "deposits" },
  { label: "Withdrawals", field: "withdrawals" },
  { label: "Purchases", field: "purchases" },
  { label: "Net flow", field: "netFlow" },
  { label: "Growth(%)", field: "growth" },
  { label: "Av. transaction value", field: "avgTransactionValue" },
];

export const revenueAnalyticsTableRows = (data: any[]) => {
  return data?.map((item: any, index: number) => ({
    sn: index + 1,
    date: item.summaryDate
      ? moment(item.summaryDate).format("MMM D,YYYY")
      : "N/A",
    totalRevenue: "\u20A6" + formatNumberWithCommas(item.totalRevenue ?? 0),
    deposits: "\u20A6" + formatNumberWithCommas(item.totalDepositRevenue ?? 0),
    withdrawals:
      "\u20A6" + formatNumberWithCommas(item.totalWithdrawalRevenue ?? 0),
    purchases:
      "\u20A6" + formatNumberWithCommas(item.totalPurchaseRevenue ?? 0),
    netFlow:
      "\u20A6" +
      formatNumberWithCommas(
        (item.totalDepositRevenue ?? 0) - (item.totalWithdrawalRevenue ?? 0),
      ),
    growth: item.growth != null ? `${item.growth}%` : "0%",
    avgTransactionValue:
      item.avgTransactionValue != null
        ? Number(item.avgTransactionValue).toFixed(2)
        : "0.00",
  }));
};

export const transactionSummaryRows = (data: any) => {
  return data?.items?.map((item: any, index: number) => ({
    sn: index + 1,
    date: moment(item.createdAt || item.date).format("MMM D,YYYY"),
    totalRevenue:
      "\u20A6" + formatNumberWithCommas(item.totalRevenue ?? item.amount ?? 0),
    deposits:
      "\u20A6" +
      formatNumberWithCommas(item.deposits ?? item.totalDepositRevenue ?? 0),
    withdrawals:
      "\u20A6" +
      formatNumberWithCommas(
        item.withdrawals ?? item.totalWithdrawalRevenue ?? 0,
      ),
    purchases:
      "\u20A6" +
      formatNumberWithCommas(item.purchases ?? item.totalPurchaseRevenue ?? 0),
    netFlow: "\u20A6" + formatNumberWithCommas(item.netFlow ?? 0),
    growth: item.growth != null ? `${item.growth}%` : "0%",
    avgTransactionValue:
      item.avgTransactionValue != null
        ? Number(item.avgTransactionValue).toFixed(2)
        : "0.00",
  }));
};
